import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Departments from "./pages/Departments";
import Doctors from "./pages/Doctors";
import Patients from "./pages/Patients";
import Appointments from "./pages/Appointments";
import CallLogs from "./pages/CallLogs";
import Settings from "./pages/Settings";
import VoIPTest from "./pages/VoIPTest";
import Waitlist from "./pages/Waitlist";
import PatientPortal from "./pages/PatientPortal";
import DoctorPortal from "./pages/DoctorPortal";
import Onboarding from "./pages/Onboarding";
import OnboardingSubmissions from "./pages/OnboardingSubmissions";
import AIPromptSettings from "./pages/AIPromptSettings";
import TelnyxConfig from "./pages/TelnyxConfig";
import Clinics from "./pages/Clinics";
import Billing from "./pages/Billing";
import Infrastructure from "./pages/Infrastructure";
import Prescriptions from "./pages/Prescriptions";
import LiveCalls from "./pages/LiveCalls";
import Analytics from "./pages/Analytics";
import Recall from "./pages/Recall";
import CostDashboard from "./pages/CostDashboard";
import ScheduleSettings from "./pages/ScheduleSettings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RegisterPartner from "./pages/RegisterPartner";
import LandingPage from "./pages/LandingPage";
import SecuritySettings from "./pages/SecuritySettings";
import PhoneSetup from "./pages/PhoneSetup";
import ComplianceReview from "./pages/ComplianceReview";
import ForceChangePassword from "./pages/ForceChangePassword";
import ChatPage from "./pages/ChatPage";
import CookieBanner from "./components/CookieBanner";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change on first login
  if (user.must_change_password) {
    return <ForceChangePassword />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Smart Home Route — landing for visitors, dashboard for logged-in users
const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user && user.must_change_password) return <ForceChangePassword />;
  return user ? <Dashboard /> : <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register-partner"
        element={
          <PublicRoute>
            <RegisterPartner />
          </PublicRoute>
        }
      />

      {/* Home: landing for visitors, dashboard for logged users */}
      <Route path="/" element={<HomeRoute />} />
      <Route
        path="/phone-setup"
        element={
          <ProtectedRoute>
            <PhoneSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments"
        element={
          <ProtectedRoute>
            <Departments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctors"
        element={
          <ProtectedRoute>
            <Doctors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Appointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waitlist"
        element={
          <ProtectedRoute>
            <Waitlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/call-logs"
        element={
          <ProtectedRoute>
            <CallLogs />
          </ProtectedRoute>
        }
      />
      
      {/* Admin only routes */}
      <Route
        path="/settings"
        element={
          <AdminRoute>
            <Settings />
          </AdminRoute>
        }
      />
      <Route
        path="/voip-test"
        element={
          <AdminRoute>
            <VoIPTest />
          </AdminRoute>
        }
      />
      <Route
        path="/onboarding-submissions"
        element={
          <AdminRoute>
            <OnboardingSubmissions />
          </AdminRoute>
        }
      />
      <Route
        path="/compliance-review"
        element={
          <AdminRoute>
            <ComplianceReview />
          </AdminRoute>
        }
      />
      <Route
        path="/ai-settings"
        element={
          <AdminRoute>
            <AIPromptSettings />
          </AdminRoute>
        }
      />
      <Route
        path="/voice-system"
        element={
          <AdminRoute>
            <TelnyxConfig />
          </AdminRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <AdminRoute>
            <Billing />
          </AdminRoute>
        }
      />
      <Route
        path="/clinics"
        element={
          <AdminRoute>
            <Clinics />
          </AdminRoute>
        }
      />
      <Route
        path="/infrastructure"
        element={
          <ProtectedRoute>
            <Infrastructure />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prescriptions"
        element={
          <ProtectedRoute>
            <Prescriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />


      {/* Patient & Doctor Portals - independent auth */}
      <Route path="/paziente/*" element={<PatientPortal />} />
      <Route path="/medico/*" element={<DoctorPortal />} />

      {/* Live Calls Dashboard */}
      <Route
        path="/live-calls"
        element={
          <ProtectedRoute>
            <LiveCalls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecuritySettings />
          </ProtectedRoute>
        }
      />

      {/* Analytics Dashboard */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />

      {/* Recall */}
      <Route
        path="/recall"
        element={
          <ProtectedRoute>
            <Recall />
          </ProtectedRoute>
        }
      />

      {/* Cost Dashboard */}
      <Route
        path="/costs"
        element={
          <ProtectedRoute>
            <CostDashboard />
          </ProtectedRoute>
        }
      />

      {/* Schedule Settings */}
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <ScheduleSettings />
          </ProtectedRoute>
        }
      />

      {/* Public Onboarding Form */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Privacy Policy - accessible without login */}
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
